import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { PaywallIndexQueryParams } from "@circle-react/helpers/urlHelpers/internalApi/search/paywalls";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { buildUpgradeAvailableParams } from "@circle-react/lib/queries/paywallSearchParams";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { PAYWALL_STATUS } from "@circle-react/types";
import type { Paywall, PaywallCurrency } from "@circle-react/types";
import { PAYWALL_NON_DRAFT_STATUSES } from "@circle-react/types/Paywall";
import type {
  PaywallOffer,
  PaywallOfferType,
} from "@circle-react/types/PaywallOffer";

// Paywall query factory. All paywall keys share the ["paywall", ...] prefix via
// paywallKey, so a single invalidation at the root covers every cache entry.
// Root string matches the wire resource name in CircleAi::Intent::RESOURCES.

export const paywallKey = createQueryKeyFactory("paywall");

// Per-page sizes re-exported so consumers share one source of truth.
export const PAYWALLS_SEARCH_PER_PAGE = 30;

interface SearchParams {
  filters?: Record<string, string | string[]>;
  initialPage?: number;
  perPage?: number;
  query?: string;
  sort?: Record<string, string>;
}

// Nullable id param accepted by the list query builders — form watch values can
// be string, number, null, or undefined.
type NullableId = string | number | null;

interface ListParams {
  communityMemberId?: NullableId;
  spaceIds?: Array<string | number | undefined>;
  status?: string[];
}

// Shared by the upsell (add-on) and upgrade picker list builders below.
interface AvailableListParams {
  currencyCode?: string;
  mainPaywallId?: NullableId;
  paywallGroupId?: NullableId;
}

interface UpgradeAvailableListParams extends AvailableListParams {
  couponId?: NullableId;
  sourceIntervalType?: string;
}

// Search params for the upsell-available list: active paywalls in the selected
// currency, optionally excluding a subscription group.
const buildUpsellsAvailableParams = (
  currencyCode?: string,
  paywallGroupId?: NullableId,
): PaywallIndexQueryParams => ({
  params: {
    filters: {
      status: PAYWALL_STATUS.ACTIVE,
      currency: currencyCode,
      ...(paywallGroupId ? { not_subscription_group: paywallGroupId } : {}),
    },
  },
});

interface PaywallSearchResponse {
  count: number;
  has_next_page: boolean;
  meta: {
    total_count: number;
  };
  page: number;
  page_count: number;
  per_page: number;
  records: Paywall[];
}

export const paywallQueries = {
  // Single paywall fetch backing the admin edit form (all tabs). Reads the one
  // record via the search endpoint filtered by paywall_id. Kept under the
  // ["paywall", ...] root so paywallInvalidations.all() (used by the Canvas AI
  // translator) refetches the open editor after an AI edit.
  formFetch: (paywallId: number) => ({
    queryKey: paywallKey.key("form-fetch", paywallId),
    queryFn: () =>
      reactQueryGet<PaywallSearchResponse>(
        internalApi.search.paywalls.index({
          params: { filters: { paywall_id: String(paywallId) } },
        }),
      ),
  }),

  // Admin settings infinite-scroll list backed by the search endpoint.
  settingsList: ({
    query = "",
    filters = {},
    perPage = PAYWALLS_SEARCH_PER_PAGE,
    initialPage = 1,
    sort = {},
  }: SearchParams = {}) => ({
    // Serialize objects to strings so all key parts satisfy string | number.
    queryKey: paywallKey.key(
      "search",
      query,
      JSON.stringify(filters),
      perPage,
      JSON.stringify(sort),
    ),
    initialPageParam: initialPage,
    queryFn: ({ pageParam = initialPage }: { pageParam?: number }) => {
      const page =
        typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;

      const searchParams: PaywallIndexQueryParams["params"] = {
        page,
        filters,
        per_page: perPage,
      };

      if (query) {
        searchParams.query = query;
      }

      if (Object.keys(sort).length > 0) {
        searchParams.order = sort;
      }

      return reactQueryGet<PaywallSearchResponse>(
        internalApi.search.paywalls.index({ params: searchParams }),
      );
    },
  }),

  // Candidate offer paywalls for the upsell tab: active paywalls in the
  // selected currency (and, when a subscription group is set, excluding that
  // group), minus the main paywall. Backs the upsell customization editor —
  // each record carries its own paywall_customization, which is the offer
  // customization the editor seeds from. Kept under the ["paywall", ...] root so
  // paywallInvalidations.all() (used by the Canvas AI translator for
  // edit_paywall_checkout_flow with target: "upsell_offer") refetches the open
  // upsell editor's source data after an AI edit, with no separate resource.
  upsellsAvailableList: ({
    mainPaywallId,
    currencyCode,
    paywallGroupId,
  }: AvailableListParams) => ({
    // Serialize nullable id params to strings to satisfy the string | number key
    // constraint and keep the key stable across renders.
    queryKey: paywallKey.key(
      "upsells-available",
      String(mainPaywallId ?? ""),
      String(paywallGroupId ?? ""),
      currencyCode ?? "",
    ),
    queryFn: async (): Promise<Paywall[]> => {
      const response = await reactQueryGet<PaywallSearchResponse>(
        internalApi.search.paywalls.index(
          buildUpsellsAvailableParams(currencyCode, paywallGroupId),
        ),
      );

      // Coerce both sides: paywall.id is numeric, but mainPaywallId is a
      // NullableId (string | number | null) from the form watch, so a string
      // "42" would never strict-equal the numeric API 42 and the main paywall
      // would leak into its own upsell-candidate list. Mirrors the Number(...)
      // coercion useUpsellOfferCustomizationRefresh uses for the same reason.
      return response.records.filter(
        paywall => Number(paywall.id) !== Number(mainPaywallId),
      );
    },
  }),

  // Candidate target paywalls for the upgrade picker: scoped to the host's
  // group, including the host itself (Decision G) — inverse of the list above.
  upgradeAvailableList: ({
    mainPaywallId,
    currencyCode,
    paywallGroupId,
    sourceIntervalType,
    couponId,
  }: UpgradeAvailableListParams) => ({
    queryKey: paywallKey.key(
      "upgrade-available",
      String(mainPaywallId ?? ""),
      String(paywallGroupId ?? ""),
      currencyCode ?? "",
      sourceIntervalType ?? "",
      String(couponId ?? ""),
    ),
    queryFn: async (): Promise<Paywall[]> => {
      const response = await reactQueryGet<PaywallSearchResponse>(
        internalApi.search.paywalls.index(
          buildUpgradeAvailableParams({
            couponId,
            currencyCode,
            mainPaywallId,
            paywallGroupId,
            sourceIntervalType,
          }),
        ),
      );

      return response.records;
    },
  }),

  // Upsell offers configured on a main paywall. Each offer carries its own
  // paywall_customization — the record edit_paywall_checkout_flow mutates for
  // target: "upsell_offer", and the source the open upsell editor seeds from.
  // Kept under the ["paywall", ...] root so paywallInvalidations.all() (the
  // Canvas AI translator's paywall invalidation) refetches the offers after an
  // AI upsell-customization edit, re-seeding the open editor with no separate
  // resource and no second invalidates declaration.
  //
  // Intentional parallel read: this query hits the exact same endpoint, params,
  // and serializer as the keyless init mutation
  // (fetchAndBuildInitialDataMutation → this factory's queryFn, same
  // OfferSerializer). Because both sources are provably identical, the seed and
  // this refetch cannot carry divergent content — only the identity/timestamp
  // fields the consuming hook strips out of the re-seed signal. Diverging this
  // query's endpoint or params from the init mutation would reintroduce that
  // drift risk; keep them in lockstep if a future editor reuses this entry.
  offers: (mainPaywallId: number | string, type: PaywallOfferType) => ({
    queryKey: paywallKey.key("offers", mainPaywallId, type),
    queryFn: () =>
      reactQueryGet<PaywallOffer[]>(
        internalApi.paywalls.offers.index({ paywall_id: mainPaywallId, type }),
      ),
  }),

  // Member-facing / simple paywalls index list.
  list: ({
    communityMemberId,
    status = [],
    spaceIds = [],
  }: ListParams = {}) => ({
    // Serialize nullable communityMemberId and array params to keep key parts as strings.
    queryKey: paywallKey.key(
      "list",
      String(communityMemberId ?? ""),
      JSON.stringify({ status, spaceIds }),
    ),
    queryFn: () =>
      reactQueryGet<Paywall[]>(
        internalApi.paywalls.index({
          params: {
            community_member_id: communityMemberId?.toString() ?? "",
            status: status.join(","),
            space_ids: spaceIds
              .filter(id => id != null)
              .map(id => String(id))
              .join(","),
          },
        }),
      ),
  }),

  // Supported paywall currencies.
  currencies: (includeTraded = false) => ({
    // Boolean serialized to string so it satisfies the string | number key constraint.
    queryKey: paywallKey.key("currencies", String(includeTraded)),
    queryFn: () =>
      reactQueryGet<PaywallCurrency[]>(
        internalApi.supportedCurrencies.index(
          includeTraded ? { include_traded: true } : {},
        ),
      ),
  }),

  // Paywall filter dropdown list — caller-supplied name scopes each distinct filter.
  filterList: (
    name: string,
    params: { status?: string[]; type?: string[] } = {},
  ) => ({
    // Embed name so that caches for different filter dropdowns are distinct but all
    // share the ["paywall", "filter"] prefix and are cleared by paywallInvalidations.all().
    queryKey: paywallKey.key("filter", name),
    queryFn: () =>
      reactQueryGet<Paywall[]>(
        internalApi.paywalls.index({
          params: {
            ...params,
            status: params.status || PAYWALL_NON_DRAFT_STATUSES,
          },
        }),
      ),
  }),
};

export const paywallInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: paywallKey.root() }),
};
